import { membersQuery, supporterPlansQuery } from "@/api/supporter";
import {
  eapResultsQuery,
  getHomeCountrySettings,
  supporterEAPPeriodsQuery,
  supporterEAPSettingsQuery,
} from "@/api/supporterEap";
import { ChartBox } from "@/components/charts/ChartBox";
import { ChartLegend } from "@/components/charts/ChartLegend";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Stat, StatLabel, StatList, StatValue } from "@/components/ui/Stat";
import { supporterPlanToFeatureSet, useSupporterFeatures } from "@/modules/supporter-plans";
import { seo } from "@/utils/seo";
import { Box, Flex, Grid, Select, Span, Spinner, Text } from "@givenwell/components";
import {
  GetEapResultsResponse,
  GetSupporterMembersResponse,
  PieDataPoint,
  SupporterEapPeriod,
} from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { barY, defineChart } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { Chart } from "@tanstack/charts/react";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, FunctionComponent, ReactNode, Suspense, useMemo, useState } from "react";
import { fallback, object, optional, string } from "valibot";

export const Route = createFileRoute("/supporter/$supporterId/eap/")({
  validateSearch: object({
    periodId: fallback(optional(string()), undefined),
  }),
  component: EAPReports,
  head: () => ({ meta: seo({ title: "EAP Reports" }) }),
});

function EAPReports() {
  const { supporterId } = Route.useParams();
  const { periodId: periodIdFromSearch } = Route.useSearch();
  const navigate = Route.useNavigate();

  function currentPeriodId() {
    const now = new Date();
    return periods.find(p => new Date(p.start_date) <= now && new Date(p.end_date) >= now)?.period_id;
  }

  const { data: periods } = useSuspenseQuery(supporterEAPPeriodsQuery(supporterId));
  const periodId = periodIdFromSearch || currentPeriodId() || periods[0]?.period_id;

  return (
    <PageWrapper
      maxWidth={1400}
      title="EAP Reports"
      rightContent={
        <>
          <Select
            css={{ width: 250 }}
            value={periodId}
            onValueChange={value => navigate({ to: ".", search: { periodId: value }, replace: true })}
          >
            {periods.map(period => (
              <option key={period.period_id} value={period.period_id}>
                {fmt.format(new Date(period.start_date))} - {fmt.format(new Date(period.end_date))}
              </option>
            ))}
          </Select>
        </>
      }
    >
      <Suspense fallback={<Spinner />}>
        <EapReportsContent supporterId={supporterId} periodId={periodId} />
      </Suspense>
    </PageWrapper>
  );
}

type ReportSectionProps = {
  period: SupporterEapPeriod;
  results: GetEapResultsResponse;
  members: GetSupporterMembersResponse;
  supporterId: string;
  enabled: boolean;
};
type ReportSection = {
  enabled: boolean;
  component: FunctionComponent<ReportSectionProps>;
};

function EapReportsContent({ supporterId, periodId }: { supporterId: string; periodId: string | undefined }) {
  const { data: periods } = useSuspenseQuery(supporterEAPPeriodsQuery(supporterId));
  const { data: members } = useSuspenseQuery(membersQuery(supporterId));
  const period = periods.find(p => p.period_id === periodId);

  if (!period) {
    throw new Error("No EAP periods found.");
  }

  const { data: results } = useSuspenseQuery(eapResultsQuery(supporterId, period.period_id || ""));

  const reportSections: ReportSection[] = [
    {
      component: UtilisationMetrics,
      enabled: true,
    },
    {
      component: UtilisationTrendChart,
      enabled: results.month_usages.some(u => u.usage_count > 0),
    },
    {
      component: SessionTopicsChart,
      enabled: false,
    },
  ];

  const firstDisabledSectionIndex = reportSections.findIndex(s => !s.enabled);

  // Only show every section up to and including the first unavailable section.
  // We render the first unavailable Section component because sections are
  // responsible for their rendering their own unavailable state.
  const visibleSections =
    firstDisabledSectionIndex > -1 ? reportSections.slice(0, firstDisabledSectionIndex + 1) : reportSections.slice();

  return (
    <Box css={{ spaceY: 16 }}>
      {visibleSections.map((section, i) => (
        <section.component
          key={i}
          period={period}
          results={results}
          members={members}
          enabled={section.enabled}
          supporterId={supporterId}
        />
      ))}
    </Box>
  );
}

const sum = (nums: number[]) => nums.reduce((acc, num) => acc + num, 0);

const fmt = new Intl.DateTimeFormat("en-nz", {
  month: "short",
  year: "numeric",
});

function UtilisationMetrics({ period, results }: ReportSectionProps) {
  const { supporterId } = Route.useParams();

  const { data: eapData } = useSuspenseQuery(supporterEAPSettingsQuery(supporterId));
  const { data: members } = useSuspenseQuery(membersQuery(supporterId));

  // Members eligible for EAP: those whose country has EAP enabled.
  // Countries without an explicit override inherit the home-country row.
  const settingsByCountry = new Map(eapData.country_settings.map(s => [s.country, s]));
  const homeSettings = getHomeCountrySettings(eapData);
  // Each eligible member gets their country's session allowance, so the total available is the sum
  // of those allowances rather than a single limit times the head count.
  let eligibleMembers = 0;
  let totalAvailableSessions = 0;
  for (const m of members.members) {
    const settings = settingsByCountry.get(m.country) ?? homeSettings;
    if (!settings?.enabled) continue;
    eligibleMembers += 1;
    totalAvailableSessions += settings.max_session_count;
  }
  // Utilisation is the share of eligible members who have used at least one session this period.
  const utilisation = eligibleMembers > 0 ? results.unique_members / eligibleMembers : null;
  const utilisationRate = utilisation === null ? null : Math.round(utilisation * 100);

  const [now] = useState(() => Date.now());
  const periodStart = new Date(period.start_date).getTime();
  const periodEnd = new Date(period.end_date).getTime();
  const elapsedFraction = (now - periodStart) / (periodEnd - periodStart);
  const periodInProgress = elapsedFraction > 0 && elapsedFraction < 1;

  return (
    <Card>
      <StatList>
        <Stat css={{ flex: "1 0 0px" }}>
          <StatLabel>Utilisation Rate</StatLabel>
          <StatValue>{utilisationRate === null ? "—" : `${utilisationRate}%`}</StatValue>
          {periodInProgress && (
            <Text css={{ fontScale: "sm", color: colors.text2 }}>
              As of{" "}
              {new Intl.DateTimeFormat("en-NZ", { day: "numeric", month: "short", year: "numeric" }).format(
                new Date(now),
              )}
            </Text>
          )}
        </Stat>
        <Stat css={{ flex: "1 0 0px" }}>
          <StatLabel>Unique Users</StatLabel>
          <StatValue>
            {results.unique_members}
            <Span css={{ fontSize: "0.67em", color: colors.text2 }}>/{eligibleMembers}</Span>
          </StatValue>
        </Stat>
        <Stat css={{ flex: "1 0 0px" }}>
          <StatLabel>Total Sessions</StatLabel>
          <StatValue>
            {results.total_usages}
            <Span css={{ fontSize: "0.67em", color: colors.text2 }}>/{totalAvailableSessions}</Span>
          </StatValue>
          {results.unique_members > 0 && (
            <Text css={{ fontScale: "sm", color: colors.text2 }}>
              {new Intl.NumberFormat("en-NZ", { maximumFractionDigits: 2 }).format(
                results.total_usages / results.unique_members,
              )}{" "}
              sessions per user on avg.
            </Text>
          )}
        </Stat>
      </StatList>
    </Card>
  );
}

function UtilisationTrendChart({ period, results, enabled }: ReportSectionProps) {
  const periodStart = period.start_date;
  const periodEnd = period.end_date;
  const monthUsages = results.month_usages;
  const counts = useMemo(() => {
    const rawWithFilledGaps: {
      month: string;
      usage_count: number;
      claim_usage_count: number;
      marketplace_usage_count: number;
    }[] = [];
    const min = new Date(periodStart);
    min.setDate(1);
    min.setHours(0, 0, 0, 0);
    const maxDate = new Date(periodEnd);
    for (let d = new Date(min); d <= maxDate; d.setMonth(d.getMonth() + 1)) {
      const month = d.getMonth() + 1; // getMonth is zero-based
      const year = d.getFullYear();
      const existing = monthUsages.find(m => m.month === month && m.year === year);
      rawWithFilledGaps.push({
        month: fmt.format(new Date(year, month - 1, 1)),
        usage_count: existing ? existing.usage_count : 0,
        claim_usage_count: existing ? existing.claim_usage_count : 0,
        marketplace_usage_count: existing ? existing.marketplace_usage_count : 0,
      });
    }
    return rawWithFilledGaps;
  }, [periodStart, periodEnd, monthUsages]);

  const max = Math.max(1, ...counts.map(d => d.usage_count));

  const definition = useMemo(() => {
    // The original chart drew a grid line at every integer, but only labelled
    // ~5 nice integer ticks (its tick format hid non-integers).
    const gridTicks = Array.from({ length: max + 1 }, (_, i) => i);
    const labelledTicks = new Set(
      scaleLinear()
        .domain([0, max])
        .ticks(5)
        .filter(tick => Number.isInteger(tick)),
    );
    return defineChart({
      marks: [
        decorative(
          barY(counts, {
            x: "month",
            y: "usage_count",
            fill: colors.cyan,
          }),
        ),
        decorative(
          barY(counts, {
            x: "month",
            y: "claim_usage_count",
            fill: colors.pink,
          }),
        ),
      ],
      scales: {
        x: {
          scale: () => scaleBand<string>().padding(0.1),
          axis: {
            ticks: { size: 0 },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
        y: {
          scale: scaleLinear().domain([0, max]),
          grid: true,
          axis: {
            line: false,
            label: "Sessions used",
            ticks: {
              values: gridTicks,
              size: 0,
              padding: 4,
              format: value => (labelledTicks.has(value) ? `${value}` : ""),
            },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
      },
    });
  }, [counts, max]);

  if (!enabled) {
    return (
      <Card title="Utilisation Trend">
        <DataUnavailable title="More data needed to unlock">
          <p>Utilisation trends will become available after at least one staff member uses an EAP session.</p>
        </DataUnavailable>
      </Card>
    );
  }

  return (
    <Card title="Utilisation Trend">
      <ChartBox>
        <Chart definition={definition} height={350} ariaLabel="Sessions used by month" />
      </ChartBox>
      <ChartLegend
        items={[
          {
            color: colors.cyan,
            label: "Sessions with Givenwell providers",
            enabled: monthUsages.some(u => u.marketplace_usage_count > 0),
          },
          {
            color: colors.pink,
            label: "Sessions submitted as claims",
            enabled: monthUsages.some(u => u.claim_usage_count > 0),
          },
        ]}
      />
    </Card>
  );
}

function SessionTopicsChart({ results, supporterId }: ReportSectionProps) {
  const topics = results.themes
    .map(t => ({ label: t.theme, count: t.count, sum: t.count }))
    .sort((a, b) => b.sum - a.sum);

  const { canViewEapTopicsReports } = useSupporterFeatures(supporterId);

  const { data: plansInOrder } = useQuery(supporterPlansQuery(supporterId));
  const firstPlanWithReports = plansInOrder?.plans.find(
    p => supporterPlanToFeatureSet[p.plan_id] && supporterPlanToFeatureSet[p.plan_id]!.canViewEapTopicsReports,
  );

  if (!canViewEapTopicsReports) {
    return (
      <Card title="Session Topics">
        <DataUnavailable title="">
          <Box css={{ spaceY: 8 }}>
            <Text>
              Upgrade to the {firstPlanWithReports?.name} plan to include reporting on session topics with our network
              providers.
            </Text>
            <Button
              as="a"
              variant="filled"
              color="primary"
              // @ts-expect-error polymorphic link
              href="mailto:support@givenwell.com"
            >
              Contact Givenwell about your plan
            </Button>
          </Box>
        </DataUnavailable>
      </Card>
    );
  }

  return (
    <Card title="Session Topics">
      {results.themes.length === 0 ? (
        <DataUnavailable title="More data needed to unlock">
          <p>To protect anonymity, your organisation must have at least:</p>
          <ul>
            <li>
              <strong>3 EAP sessions within the Givenwell network</strong> during this period
            </li>
          </ul>
        </DataUnavailable>
      ) : (
        <Flex css={{ gap: 24 }}>
          <Box css={{ flex: "1 0 0px", maxWidth: 650 }}>
            <PopularityBarChart label="Topic" data={topics} />
          </Box>
        </Flex>
      )}
    </Card>
  );
}

function DataUnavailable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box
      css={{
        m: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        css={{
          backgroundColor: colors.yellowAccent,
          color: colors.yellow900,
          p: 24,
          maxWidth: 430,
          borderRadius: 12,
          "& ul": {
            mt: 4,
            pl: 24,
            listStyleType: "disc",
          },
        }}
      >
        <Text css={{ weight: 700, fontScale: "xl", mb: 12 }}>{title}</Text>
        {children}
      </Box>
    </Box>
  );
}

function PopularityBarChart({ label, data }: { label: string; data: PieDataPoint[] }) {
  const categoryTotal = sum(data.map(d => d.sum));
  const categoryMax = Math.max(...data.map(d => d.sum));

  return (
    <Box>
      <Grid css={{ gridTemplateColumns: "auto 1fr auto", rowGap: 2, columnGap: 12, alignItems: "center" }}>
        {/* <>
          <Box css={{ flex: "1 0 0px", position: "relative", px: 0 }}>{label}</Box>
          <Box css={{ textAlign: "right", position: "relative", gridColumn: "2 / -1" }}>Percentage of sessions</Box>
        </> */}
        {data.map((category, i) => {
          const percentage = (category.sum / categoryTotal) * 100;
          const visualPercentage = (category.sum / categoryMax) * 100;

          return (
            <Fragment key={category.label}>
              <Box css={{ px: 0, fontScale: "sm", letterSpacing: "0.01em" }}>
                {formatCategoryLabelText(category.label)}
              </Box>
              <Box
                css={{
                  flex: "1 0 0px",
                  position: "relative",
                  height: 26,
                  borderRadius: 6,
                  overflow: "auto",
                  width: "100%",
                  backgroundColor: colors.warmGreyHalf,
                }}
              >
                <Box
                  css={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    borderRadius: "inherit",
                    width: visualPercentage + "%",
                    backgroundColor: colors.cyan,
                  }}
                ></Box>
              </Box>
              <Box
                css={{
                  width: 50,
                  textAlign: "right",
                  position: "relative",
                  fontScale: "sm",
                  letterSpacing: "0.01em",
                  mr: 4,
                }}
              >
                {percentage.toFixed(0)}%
              </Box>
            </Fragment>
          );
        })}
      </Grid>
    </Box>
  );
}

function formatCategoryLabelText(text: string) {
  const [category, ...subcategories] = text.split("|");
  const subcategory = subcategories.join("|");
  if (!subcategory) return category;
  if (subcategory === "Other") return `Other ${category}`;
  return subcategory;
}
